import { freeTextSchema, personNameSchema, postalCodeSchema, TEXT_MAX_LENGTH } from '@1trading/fast-api-domain/forms';
import { z } from 'zod';

import { createTaxResidencesSchema, TAX_RESIDENCE_DEFAULTS, taxResidenceApiToTaxResidence, taxResidenceToTaxResidenceApi } from '@/features/tax-residency/schema';
import i18n from '@/lib/i18n';
import { getCountryPostalCodeUse, mainstreamCountrySchema } from '@/services/api/mainstream';
import { type Identity, type IdentityWithFallbacks, identityWithFallbacksSchema } from '@/services/api/profile';
import type { EmptyString } from '@/types';
import { blankAsAbsent } from '@/utils/blank-as-absent';
import { toDatePartsFromString, toDateStringFromParts } from '@/utils/date';
import { nameErrors, postalCodeErrors, textErrors } from '@/utils/error-messages';
import { dateOfBirthRefinement } from './refinements/date-of-birth';
import { phoneNumberRefinement } from './refinements/phone-number';
import { postalCodeRefinement, postalCodeRefinementParams } from './refinements/postal-code';

const addressLineSchema = freeTextSchema({ maxLength: TEXT_MAX_LENGTH.SHORT, errors: textErrors('personalInformation.errors.address') });
// fast-api stores the address lines joined with commas, so a comma would split a line when it's read back
const withoutCommas = (line: string | undefined) => line?.replace(/,/g, '');

const personalInformationBaseSchema = z.object({
  firstName: personNameSchema({ errors: nameErrors('personalInformation.errors.firstName') }),
  lastName: personNameSchema({ errors: nameErrors('personalInformation.errors.lastName') }),
  citizenship: mainstreamCountrySchema(i18n.t('personalInformation.errors.citizenship')),
  citizenship2: blankAsAbsent(mainstreamCountrySchema()),
  dateOfBirth: z
    .object({
      day: z.string().regex(/^\d{1,2}$/, {
        error: i18n.t('personalInformation.errors.dateOfBirthDay'),
      }),
      month: z.string().regex(/^\d{1,2}$/, {
        error: i18n.t('personalInformation.errors.dateOfBirthMonth'),
      }),
      year: z.string().regex(/^\d{4}$/, {
        error: i18n.t('personalInformation.errors.dateOfBirthYear'),
      }),
    })
    .superRefine(dateOfBirthRefinement),
  townOfBirth: freeTextSchema({ maxLength: TEXT_MAX_LENGTH.SHORT, errors: textErrors('personalInformation.errors.townOfBirth') }),
  countryOfBirth: mainstreamCountrySchema(i18n.t('personalInformation.errors.countryOfBirth')),
  countryOfResidency: mainstreamCountrySchema(i18n.t('personalInformation.errors.countryOfResidency')),
  address: z.string().transform(withoutCommas).pipe(addressLineSchema),
  address2: z.string().optional().transform(withoutCommas).pipe(blankAsAbsent(addressLineSchema)),
  // Whether one is required depends on the country, which postalCodeRefinement checks against the normalised value
  postCode: postalCodeSchema({ errors: postalCodeErrors('personalInformation.errors.postCodeInvalid') }),
  city: freeTextSchema({ maxLength: TEXT_MAX_LENGTH.SHORT, errors: textErrors('personalInformation.errors.city') }),
  taxId: z.string().optional(),
  taxResidences: createTaxResidencesSchema(),
  areaCode: z.string().min(1, {
    error: i18n.t('personalInformation.errors.areaCode'),
  }),
  phone: z.string().min(1, {
    error: i18n.t('personalInformation.errors.mobilePhone'),
  }),
});

export const personalInformationSchema = personalInformationBaseSchema.refine(postalCodeRefinement, postalCodeRefinementParams).superRefine(phoneNumberRefinement);

export type PersonalInformation = z.infer<typeof personalInformationSchema>;

export type PersonalInformationDefaults = Omit<PersonalInformation, 'dateOfBirth'> & {
  dateOfBirth: {
    [K in keyof PersonalInformation['dateOfBirth']]: PersonalInformation['dateOfBirth'][K] | EmptyString;
  };
};

export type PersonalInformationOrDefaults = PersonalInformation | PersonalInformationDefaults;

export const personalInformationCountryOfResidencySchema = personalInformationBaseSchema.pick({
  countryOfResidency: true,
});

export type PersonalInformationCountryOfResidency = z.infer<typeof personalInformationCountryOfResidencySchema>;

export const personalInformationAboutYouSchema = personalInformationBaseSchema.pick({
  firstName: true,
  lastName: true,
  dateOfBirth: true,
  citizenship: true,
  citizenship2: true,
  townOfBirth: true,
  countryOfBirth: true,
});

export type PersonalInformationAboutYou = z.infer<typeof personalInformationAboutYouSchema>;

export const personalInformationHomeAddressSchema = personalInformationBaseSchema
  .pick({
    // countryOfResidency isn't rendered in this step, but is required for the refinement
    countryOfResidency: true,
    address: true,
    address2: true,
    postCode: true,
    city: true,
  })
  .refine(postalCodeRefinement, postalCodeRefinementParams);

export type PersonalInformationHomeAddress = z.infer<typeof personalInformationHomeAddressSchema>;

export const personalInformationMobilePhoneSchema = personalInformationBaseSchema
  .pick({
    areaCode: true,
    phone: true,
  })
  .superRefine(phoneNumberRefinement);

export type PersonalInformationMobilePhone = z.infer<typeof personalInformationMobilePhoneSchema>;

export const personalInformationTaxResidencySchema = personalInformationBaseSchema.pick({
  taxResidences: true,
});

export type PersonalInformationTaxResidency = z.infer<typeof personalInformationTaxResidencySchema>;

export const identityToPersonalInformationWithDefaults = identityWithFallbacksSchema.transform((identity: Identity | IdentityWithFallbacks): PersonalInformationOrDefaults => {
  return {
    firstName: identity.firstName,
    lastName: identity.lastName,
    citizenship: identity.citizenship,
    citizenship2: identity.citizenship2,
    dateOfBirth: toDatePartsFromString(identity.birthInfo.date),
    townOfBirth: identity.birthInfo.locality,
    countryOfBirth: identity.birthInfo.country,
    countryOfResidency: identity.addressOfResidency.country,
    address: identity.addressOfResidency.addressLines[0] || '',
    address2: identity.addressOfResidency.addressLines[1] || '',
    postCode: identity.addressOfResidency.postalCode,
    city: identity.addressOfResidency.locality,
    taxId: identity.taxId || '',
    taxResidences: identity.taxResidency?.residences.map(taxResidenceApiToTaxResidence) ?? [TAX_RESIDENCE_DEFAULTS],
    areaCode: identity.phoneNumber.areaCode,
    phone: identity.phoneNumber.mobilePhone,
  };
});

export const personalInformationToIdentity = personalInformationSchema.transform(
  async (data): Promise<Identity> => ({
    firstName: data.firstName,
    lastName: data.lastName,
    citizenship: data.citizenship,
    citizenship2: data.citizenship2,
    birthInfo: {
      date: toDateStringFromParts(data.dateOfBirth),
      locality: data.townOfBirth,
      country: data.countryOfBirth,
    },
    addressOfResidency: {
      addressLines: [data.address, ...(data.address2 ? [data.address2] : [])],
      country: data.countryOfResidency,
      locality: data.city,
      // The field is hidden for a country that uses none, so a change of country can leave one the customer can't see
      postalCode: (await getCountryPostalCodeUse(data.countryOfResidency)) === 'none' ? '' : data.postCode,
    },
    phoneNumber: {
      areaCode: data.areaCode,
      mobilePhone: data.phone,
    },
    taxResidency: {
      residences: data.taxResidences.map(taxResidenceToTaxResidenceApi),
    },
  }),
);
